import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Testimonial & Review Schema for SEO
 * Optimizes testimonials for Google, Bing, and AI search
 */
const TestimonialSEO = ({ testimonials = [] }) => {
  if (testimonials.length === 0) return null;

  const businessName = 'NY Pest & Exterminating';
  const baseUrl = 'https://nypestexterminating.com';

  // Calculate aggregate rating
  const totalRating = testimonials.reduce((sum, t) => sum + t.rating, 0);
  const averageRating = (totalRating / testimonials.length).toFixed(1);

  // AggregateRating Schema - Shows star ratings in search results
  const aggregateRatingSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://nypestexterminating.com/#localbusiness",
    "name": businessName,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "New York City",
      "addressLocality": "New York",
      "addressRegion": "NY",
      "postalCode": "11691",
      "addressCountry": "US"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": testimonials.length
    }
  };

  // Individual Review Schemas - Each testimonial as a review
  const reviewSchemas = testimonials.slice(0, 10).map(testimonial => ({
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": {
      "@type": "LocalBusiness",
      "name": businessName,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "New York",
        "addressRegion": "NY"
      }
    },
    "author": {
      "@type": "Person",
      "name": testimonial.name
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": testimonial.rating,
      "bestRating": "5",
      "worstRating": "1"
    },
    "reviewBody": testimonial.text,
    "datePublished": testimonial.created_at
  }));

  // Organization Schema with Reviews
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": businessName,
    "url": baseUrl,
    "logo": `${baseUrl}/logo.png`,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "reviewCount": testimonials.length,
      "bestRating": "5"
    },
    "review": testimonials.slice(0, 5).map(t => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": t.name
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": t.rating
      },
      "reviewBody": t.text
    }))
  };

  return (
    <Helmet>
      {/* Meta tags for ratings */}
      <meta name="rating" content={averageRating} />
      <meta name="review-count" content={testimonials.length} />
      
      {/* Aggregate Rating Schema */}
      <script type="application/ld+json">
        {JSON.stringify(aggregateRatingSchema)}
      </script>

      {/* Organization with Reviews Schema */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      {/* Individual Review Schemas */}
      {reviewSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default TestimonialSEO;
